import type {
  ClientPortalAuthConfig,
  ClientPortalBilling,
  ClientPortalCase,
  ClientPortalDocument,
  ClientPortalLoginChallenge,
  ClientPortalLoginResponse,
} from "@duralex/shared";

/**
 * The portal token is a different credential for a different principal, so
 * it lives under its own key. Sharing the staff key is how a client would
 * end up holding a staff session, or the reverse.
 */
export const CLIENT_TOKEN_KEY = "duralex_client_token";

// A browser told to block site data throws from the localStorage getter
// itself, and RequireClientAuth reads the token during render: unguarded,
// that setting turns the portal into a white screen instead of a sign-in
// page. Guarded here, at the one boundary, rather than at each caller.
export function getClientToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(CLIENT_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearClientToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CLIENT_TOKEN_KEY);
  } catch {
    /* storage we cannot reach is storage that holds nothing */
  }
}

function clientHeaders(): Record<string, string> {
  const token = getClientToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Every portal auth call answers the same way: it worked, or here are the words to show. */
export type ClientPortalResult = { ok: true } | { ok: false; error: string };

/** The password door may also answer half-way: right password, mailed code still owed. */
export type ClientPortalSessionResult = ClientPortalResult | { ok: "code"; pendingToken: string };

// Neutral on purpose: this screen may be asking for a password, a case ID
// or a reset, and it is reached when the server answered with no words of
// its own at all.
const FALLBACK_ERROR = "We could not complete that. Please try again.";
const OFFLINE_ERROR = "We could not reach the office. Please try again in a moment.";
const STORAGE_ERROR =
  "This browser is blocking the storage we use to keep you signed in. Allow site data for this page, then sign in.";
const MAIL_DOWN_ERROR =
  "We cannot send email just now. Please call our office and we will help you sign in.";
// Fifteen: the issue budget is 3 per rolling 15 minutes, so "a few
// minutes" would invite a retry the server is certain to refuse.
const CODE_THROTTLED_ERROR =
  "We have just sent you several codes. Please use the newest one, or ask again in about fifteen minutes.";

/**
 * The server answers every wrong credential with one generic message on
 * purpose - it must not confirm which matters exist - so show its words
 * rather than inventing our own.
 */
async function postForSession(path: string, body: unknown): Promise<ClientPortalSessionResult> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, error: OFFLINE_ERROR };
  }

  const parsed = (await res.json().catch(() => ({}))) as Partial<ClientPortalLoginResponse> &
    Partial<ClientPortalLoginChallenge> & {
      error?: string;
    };

  // Half signed in: the password was right and a mailed code is owed. The
  // pending token is not a session and must never reach storage.
  if (res.ok && typeof parsed.otp?.pendingToken === "string" && parsed.otp.pendingToken) {
    return { ok: "code", pendingToken: parsed.otp.pendingToken };
  }

  if (!res.ok || !parsed.token) {
    return { ok: false, error: parsed.error ?? FALLBACK_ERROR };
  }

  try {
    window.localStorage.setItem(CLIENT_TOKEN_KEY, parsed.token);
  } catch {
    // Blocked site data. Worth its own message rather than an exception:
    // the set-password link is spent by the time we get here, so a client
    // left staring at a frozen button would need a new one from the office.
    return { ok: false, error: STORAGE_ERROR };
  }
  return { ok: true };
}

// The doors that never owe a code; a challenge from one of them is a
// server this build does not understand, refused rather than obeyed.
async function postForPlainSession(path: string, body: unknown): Promise<ClientPortalResult> {
  const result = await postForSession(path, body);
  return result.ok === "code" ? { ok: false, error: FALLBACK_ERROR } : result;
}

/** The credential the office hands out, offered until the firm closes that door in Settings. */
export function loginClient(eId: string, dob: string): Promise<ClientPortalResult> {
  return postForPlainSession("/api/client/auth/login", { eId, dob });
}

/** The account credential the invitation sets up. Same door, same answers - or the code step. */
export function loginClientWithEmail(
  email: string,
  password: string,
): Promise<ClientPortalSessionResult> {
  return postForSession("/api/client/auth/login", { email, password });
}

/** The mailed code alone, for the client who never set a password. */
export function loginClientWithCode(email: string, code: string): Promise<ClientPortalResult> {
  return postForPlainSession("/api/client/auth/login", { email, code });
}

/** Finishes the two-step sign-in: the pending token names the account, the code proves the mailbox. */
export function verifyLoginCode(pendingToken: string, code: string): Promise<ClientPortalResult> {
  return postForPlainSession("/api/client/auth/login/otp", { pendingToken, code });
}

/** Spends the emailed link: sets the password and signs the client in. */
export function setPortalPassword(token: string, password: string): Promise<ClientPortalResult> {
  return postForPlainSession("/api/client/auth/set-password", { token, password });
}

async function postForCodeSend(path: string, body: unknown): Promise<ClientPortalResult> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, error: OFFLINE_ERROR };
  }
  const parsed = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    reason?: string;
    error?: string;
  };
  if (!res.ok) return { ok: false, error: parsed.error ?? FALLBACK_ERROR };
  if (parsed.ok === false) {
    return {
      ok: false,
      error: parsed.reason === "throttled" ? CODE_THROTTLED_ERROR : MAIL_DOWN_ERROR,
    };
  }
  return { ok: true };
}

/** Asks for the code owed after the password. Past the password, so the answers are honest. */
export function requestLoginCode(pendingToken: string): Promise<ClientPortalResult> {
  return postForCodeSend("/api/client/auth/login/email-code", { pendingToken });
}

/** Asks for a sign-in code with no password involved. The server answers every address alike. */
export function requestEmailCode(email: string): Promise<ClientPortalResult> {
  return postForCodeSend("/api/client/auth/email-code", { email });
}

// Quiet on failure: the extra door just does not render, and the case ID
// door stays offered - the server is the gate either way.
const QUIET_CONFIG: ClientPortalAuthConfig = {
  otpLogin: false,
  caseIdLogin: true,
  firmName: null,
  logoVersion: null,
};

/** What the sign-in screen should offer. */
export async function fetchPortalAuthConfig(): Promise<ClientPortalAuthConfig> {
  try {
    const res = await fetch("/api/client/auth/config", { credentials: "same-origin" });
    if (!res.ok) return QUIET_CONFIG;
    const body = (await res.json().catch(() => null)) as {
      otpLogin?: unknown;
      caseIdLogin?: unknown;
      firmName?: unknown;
      logoVersion?: unknown;
    } | null;
    const str = (v: unknown) => (typeof v === "string" ? v : null);
    return {
      otpLogin: body?.otpLogin === true,
      caseIdLogin: body?.caseIdLogin !== false,
      firmName: str(body?.firmName),
      logoVersion: str(body?.logoVersion),
    };
  } catch {
    return QUIET_CONFIG;
  }
}

/**
 * Asks for a fresh password link. The server answers 200 to every address,
 * on file or not; ok:false in the body means the firm's mail is down -
 * a fact about the firm, never about the address.
 */
export async function requestPasswordLink(email: string): Promise<ClientPortalResult> {
  let res: Response;
  try {
    res = await fetch("/api/client/auth/forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ email }),
    });
  } catch {
    return { ok: false, error: OFFLINE_ERROR };
  }
  if (!res.ok) {
    return { ok: false, error: "We could not send that just now. Please try again in a moment." };
  }
  const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
  if (body?.ok === false) {
    return { ok: false, error: MAIL_DOWN_ERROR };
  }
  return { ok: true };
}

/** Thrown when the portal token is gone or no longer accepted. */
export const SESSION_EXPIRED = "session-expired";
/** Thrown when a checkout is refused because the firm switched billing off:
    the token is still good, so the page shows its own caption. */
export const PAYMENT_UNAVAILABLE = "payment-unavailable";

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: clientHeaders(), credentials: "same-origin" });
  // requireClient re-checks accessEnabled on every request, so a revoked or
  // expired session must not leave a dead token behind.
  if (res.status === 401 || res.status === 403) {
    clearClientToken();
    throw new Error(SESSION_EXPIRED);
  }
  if (!res.ok) throw new Error(`Request failed (HTTP ${res.status})`);
  return (await res.json()) as T;
}

export function fetchClientCase(): Promise<ClientPortalCase> {
  return getJson<ClientPortalCase>("/api/client/case");
}

export function fetchClientDocuments(): Promise<ClientPortalDocument[]> {
  return getJson<ClientPortalDocument[]>("/api/client/documents");
}

export function fetchClientBilling(returning = false): Promise<ClientPortalBilling> {
  // returning=1 asks the server to check the just-made payment with Stripe
  // right away instead of waiting out the webhook grace period.
  return getJson<ClientPortalBilling>(`/api/client/billing${returning ? "?returning=1" : ""}`);
}

/**
 * Starts (or resumes) paying an invoice; the server answers with the hosted
 * Checkout URL to navigate to. A plain link cannot carry the portal token,
 * so the caller does window.location.assign with what we return.
 */
export async function startInvoiceCheckout(invoiceId: string): Promise<string> {
  const res = await fetch(`/api/client/billing/invoices/${invoiceId}/checkout`, {
    method: "POST",
    headers: clientHeaders(),
    credentials: "same-origin",
  });
  if (res.status === 403) {
    // Any other 403 is a revoked client and signs out below.
    const refusal = (await res.json().catch(() => null)) as { code?: string } | null;
    if (refusal?.code === "module_not_entitled") throw new Error(PAYMENT_UNAVAILABLE);
  }
  if (res.status === 401 || res.status === 403) {
    clearClientToken();
    throw new Error(SESSION_EXPIRED);
  }
  const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !body.url) {
    throw new Error(body.error ?? "Could not start the payment - please try again.");
  }
  return body.url;
}

/**
 * The download routes are behind the portal token, so a plain link cannot
 * fetch them - we pull the bytes and hand the browser a blob to save.
 */
async function downloadPortalFile(url: string, fileName: string): Promise<void> {
  const res = await fetch(url, {
    headers: clientHeaders(),
    credentials: "same-origin",
  });
  if (res.status === 401 || res.status === 403) {
    clearClientToken();
    throw new Error(SESSION_EXPIRED);
  }
  if (!res.ok) throw new Error("We could not open that file. Please try again.");

  const blobUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

export async function downloadClientDocument(id: string, fileName: string): Promise<void> {
  return downloadPortalFile(`/api/client/documents/${encodeURIComponent(id)}/download`, fileName);
}

export async function downloadClientInvoicePdf(id: string, number?: string | null): Promise<void> {
  return downloadPortalFile(
    `/api/client/billing/invoices/${encodeURIComponent(id)}/pdf`,
    `${number ?? "invoice"}.pdf`,
  );
}

export async function downloadClientReceiptPdf(id: string, number?: string | null): Promise<void> {
  return downloadPortalFile(
    `/api/client/billing/payments/${encodeURIComponent(id)}/receipt.pdf`,
    `receipt-${number ?? "payment"}.pdf`,
  );
}

/** Mirrors config/clientUpload.ts - refuse locally what the server refuses. */
// The portal ceiling is deliberately tighter than the firm one
// (config/clientUpload.ts vs env.MAX_FILE_SIZE).
export const PORTAL_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export { UPLOAD_ACCEPT } from "./documents";

/** Resolves with the server's confirmation - it owns the words, as it does on refusal. */
export async function uploadClientDocument(file: File): Promise<string> {
  const form = new FormData();
  // The field name multer is bound to; no Content-Type header, or the
  // multipart boundary the browser generates never reaches the server.
  form.append("file", file);
  const res = await fetch("/api/client/documents/upload", {
    method: "POST",
    headers: clientHeaders(),
    credentials: "same-origin",
    body: form,
  });
  if (res.status === 401 || res.status === 403) {
    clearClientToken();
    throw new Error(SESSION_EXPIRED);
  }

  const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
  if (!res.ok) {
    throw new Error(body.error ?? "We could not accept that file. Please try again.");
  }
  return body.message ?? "Thank you. We have your file.";
}
